import {
  Copy,
  EyeIcon,
  FileText,
  Search,
  User,
  Filter,
  ListOrdered,
  Download,
} from "lucide-react";
import React, { useState } from "react";
import { useEffect } from "react";
import { getMandateReport } from "../../services/apiService";
import ModernLoader from "../../components/Loader/ModernLoader";
import ViewMandate from "../../components/modals/ViewMandate";

function MandateReport() {
  const [reportData, setReportData] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(null);
  const [page, setPage] = useState(1);
  const [isCopied, setIsCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [search, setSearch] = useState("");
  const [searchByUserId, setSearchByUserId] = useState("");
  const [debouncedUserIdSearch, setDebouncedUserIdSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [tableData, setTableData] = useState(null);
  const [viewData, setViewData] = useState(false);

  const statusStyles = {
    PENDING: "bg-yellow-100 text-yellow-700",
    ACTIVATED: "bg-green-100 text-green-700",
    active: "bg-green-100 text-green-700",
    CREATED: "bg-green-100 text-green-700",
    FINISHED: "bg-blue-100 text-blue-700",
    FAILED: "bg-red-100 text-red-700",
    CANCELLED: "bg-red-100 text-red-700",
  };

  const handleCopy = () => {};

  const handleClear = () => {
    setSearchByUserId("");
    setPerPage(10);
    setDebouncedSearch("");
    setSelectedStatus("");
    setSearch("");
    setPage(1);
  };

  const fetchMandateReport = async () => {
    let payload = {};

    if (debouncedSearch) payload.search = debouncedSearch;
    if (debouncedUserIdSearch) payload.user_id = debouncedUserIdSearch;
    if (perPage) payload.per_page = perPage;
    if (page) payload.page = page;
    if (selectedStatus) payload.status = selectedStatus;

    setLoading(true);
    try {
      const res = await getMandateReport(payload);

      if (res?.status?.code === 200) {
        setReportData(res);
        setTableData(res?.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMandateReport();
  }, [perPage, page, debouncedSearch, selectedStatus, debouncedUserIdSearch]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search);
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [search]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedUserIdSearch(searchByUserId);
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [searchByUserId]);

  return (
    <div className="p-4">
      {loading && <ModernLoader />}
      <div className="space-y-6 p-6 bg-slate-50/70 rounded-2xl border border-slate-200">
        {/* Top Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-600 rounded-xl shadow-lg shadow-indigo-200">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                Mandate Report
              </h1>
              <p className="text-sm text-slate-500 font-medium">
                View and manage authorized legal instructions.
              </p>
            </div>
          </div>

      
        </div>

        {/* Filter Card Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-slate-100">
            {/* Search Field */}
            <div className="p-4 group">
              <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 group-focus-within:text-indigo-600 transition-colors">
                <Search className="w-3 h-3" />
                Quick Search
              </label>
              <input
                type="search"
                placeholder="Name, ID, or Ref..."
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent text-sm font-medium text-slate-700 outline-none placeholder:text-slate-300"
              />
            </div>

            {/* User ID Field */}
            <div className="p-4 group">
              <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 group-focus-within:text-indigo-600 transition-colors">
                <User className="w-3 h-3" />
                User ID
              </label>
              <input
                type="text"
                value={searchByUserId}
                onChange={(e) =>
                  setSearchByUserId(e.target.value.toUpperCase())
                }
                placeholder="EX: APID1036"
                className="w-full bg-transparent text-sm font-medium text-slate-700 outline-none uppercase placeholder:text-slate-300"
              />
            </div>

            {/* Status Filter */}
            <div className="p-4 group">
              <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 group-focus-within:text-indigo-600 transition-colors">
                <Filter className="w-3 h-3" />
                Status
              </label>
              <select
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full bg-transparent text-sm font-semibold text-slate-700 outline-none cursor-pointer appearance-none"
              >
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="ACTIVE">Active</option>
                <option value="CREATED">Created</option>
                <option value="ACTIVATED">Activated</option>
                <option value="FINISHED">Finished</option>
                <option value="FAILED">Failed</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="PAUSED">Paused</option>
                <option value="partially_collected">Partially Collected</option>
                <option value="PARTIALLY_COMPLETED">Partially Completed</option>
              </select>
            </div>

            {/* Per Page */}
            <div className="p-4 group">
              <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 group-focus-within:text-indigo-600 transition-colors">
                <ListOrdered className="w-3 h-3" />
                Per Page
              </label>
              <select
                onChange={(e) => setPerPage(e.target.value)}
                className="w-full bg-transparent text-sm font-semibold text-slate-700 outline-none cursor-pointer appearance-none"
              >
                <option value="10">10 Rows</option>
                <option value="20">20 Rows</option>
                <option value="50">50 Rows</option>
                <option value="100">100 Rows</option>
              </select>
            </div>

            {/* Refresh/Clear Actions (Optional spacer for 5th col) */}
            <div className="p-4 flex items-end justify-center lg:justify-end">
              <button
                onClick={handleClear}
                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 underline underline-offset-4"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {tableData?.length > 0 && (
        <div className="w-full px-3 md:px-6">
          <div className="mt-6 w-full overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
            <table className="min-w-[700px] w-full text-xs text-left text-gray-700">
              <thead className="bg-gray-100 text-gray-600 uppercase tracking-wider sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Mobile</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Frequency</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Start</th>
                  <th className="px-4 py-3">End</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3 text-center">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 bg-white">
                {tableData?.map((item, index) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 ${
                      index % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                    }`}
                  >
                    <td className="px-4 py-3 font-medium">{item.id}</td>

                    <td className="px-4 py-3">
                      {item.name || item.user?.name}
                    </td>

                    <td className="px-4 py-3">{item.mobile_number}</td>

                    <td className="px-4 py-3">₹{item.amount}</td>

                    <td className="px-4 py-3">{item.frequency}</td>

                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-[10px] font-semibold ${
                          statusStyles[item.status] ||
                          "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      {new Date(item.start_date).toLocaleDateString()}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      {new Date(item.end_date).toLocaleDateString()}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      {new Date(item.created_at).toLocaleString()}
                    </td>

                    {/* 👁 VIEW BUTTON */}
                    <td className="px-4 py-3 text-center">
                      <button
                        className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full"
                        onClick={() => setViewData(item)}
                      >
                        <EyeIcon size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reportData?.pagination && (
        <div className="w-full flex justify-center">
          <div className="flex items-center w-fit justify-center mt-4 px-2 bg-slate-100 p-4 rounded-lg text-sm text-gray-600">
            <span>
              Page {reportData?.pagination.current_page} of{" "}
              {reportData?.pagination.last_page}
            </span>

            <div className="flex gap-2">
              <button
                disabled={!reportData?.pagination.prev_page_url}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 rounded border disabled:opacity-40"
              >
                Prev
              </button>

              <button
                disabled={!reportData?.pagination.next_page_url}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 rounded border disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      <ViewMandate data={viewData} setMandateData={() => setViewData(null)} />
    </div>
  );
}

export default MandateReport;
